import { useEffect, useState } from 'react';
import Dialogo from '@/components/Dialogo';
import { gerarSenhaTemporaria, recusarSenha } from '@/lib/senhaTemporaria';
import { redefinirSenha } from '@/services/usuariosService';

/**
 * O gestor define uma senha temporária para um colaborador.
 *
 * O fluxo é "define e entrega": o gestor escolhe (ou gera) a senha, passa para a
 * pessoa, e ela troca depois. Não é e-mail de redefinição — a clínica cadastra
 * colaborador com e-mail que às vezes é da própria clínica, e depender da caixa
 * de entrada travaria o atendimento no meio do expediente.
 *
 * A senha fica visível na tela **de propósito**: ela precisa ser copiada ou
 * ditada. Some ao fechar o diálogo, e nunca é gravada em lugar nenhum.
 */

const MENSAGENS_ERRO = {
  'functions/permission-denied': 'Só o gestor desta clínica pode redefinir senhas.',
  'functions/not-found': 'Este usuário não está mais na equipe.',
  'functions/invalid-argument': 'A senha não foi aceita pelo servidor.',
  'functions/internal': 'Não foi possível falar com o servidor. Tente de novo.',
};

export default function DialogoRedefinirSenha({ clinicaId, membro, aberto, aoFechar }) {
  const [senha, setSenha] = useState('');
  const [gerada, setGerada] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);
  const [pronto, setPronto] = useState(false);
  const [copiado, setCopiado] = useState(false);

  // Toda abertura começa limpa: a senha da pessoa anterior não pode sobrar na
  // tela quando o gestor abre o diálogo de outra.
  useEffect(() => {
    if (!aberto) return;
    setSenha('');
    setGerada(false);
    setErro(null);
    setPronto(false);
    setCopiado(false);
  }, [aberto, membro?.id]);

  if (!membro) return null;

  const problema = recusarSenha(senha);

  async function confirmar() {
    if (problema) {
      setErro(problema);
      return;
    }
    setEnviando(true);
    setErro(null);
    try {
      await redefinirSenha({ clinicaId, uid: membro.id, senha });
      setPronto(true);
    } catch (e) {
      // O objeto de erro pode carregar o que foi enviado; só o código e a
      // mensagem tratada saem daqui.
      console.error('[OdontoSDR] Falha ao redefinir senha:', e?.code);
      setErro(MENSAGENS_ERRO[e?.code] ?? e?.message ?? 'Não foi possível redefinir a senha.');
    } finally {
      setEnviando(false);
    }
  }

  async function copiar() {
    try {
      await navigator.clipboard.writeText(senha);
      setCopiado(true);
    } catch {
      // Sem permissão de área de transferência: a senha está visível na tela e
      // pode ser selecionada à mão. Não é motivo para mostrar erro.
      setCopiado(false);
    }
  }

  return (
    <Dialogo
      titulo={`Redefinir senha — ${membro.nome || membro.email}`}
      aberto={aberto}
      aoFechar={aoFechar}
      largura={480}
    >
      <div className="dialogo-corpo">
        {pronto ? (
          <>
            <p>
              Senha redefinida. Entregue a nova senha para{' '}
              <strong>{membro.nome || membro.email}</strong>.
            </p>
            {/* A senha continua visível: é agora que o gestor a copia ou dita. */}
            <div className="senha-caixa">
              <code>{senha}</code>
              <button type="button" className="btn-texto" onClick={copiar}>
                {copiado ? 'Copiado ✓' : 'Copiar'}
              </button>
            </div>
            <p className="campo-ajuda">
              As sessões abertas dela foram encerradas — ela vai precisar entrar de novo
              com esta senha, e pode trocá-la depois.
            </p>
            <div className="dialogo-acoes">
              <button type="button" className="btn-primario" onClick={aoFechar}>
                Fechar
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="campo">
              <label htmlFor="senha-nova">Senha nova</label>
              <div className="senha-linha">
                <input
                  id="senha-nova"
                  // `text`, não `password`: o gestor precisa LER o que vai
                  // entregar. Esconder aqui só provocaria erro de digitação numa
                  // senha que ele mesmo vai ditar.
                  type="text"
                  value={senha}
                  onChange={(e) => {
                    setSenha(e.target.value);
                    setGerada(false);
                    setErro(null);
                  }}
                  placeholder="ao menos 8 caracteres"
                  autoComplete="off"
                  disabled={enviando}
                />
                <button
                  type="button"
                  className="btn-secundario compacto"
                  onClick={() => {
                    setSenha(gerarSenhaTemporaria());
                    setGerada(true);
                    setErro(null);
                  }}
                  disabled={enviando}
                >
                  Gerar
                </button>
              </div>
              <p className="campo-ajuda">
                {gerada
                  ? 'Senha temporária gerada — copie ou anote antes de confirmar.'
                  : 'Você define a senha e a entrega à pessoa; ela troca depois.'}
              </p>
            </div>

            {erro && <p className="erro">{erro}</p>}

            <div className="dialogo-acoes">
              <button type="button" className="btn-texto" onClick={aoFechar} disabled={enviando}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primario"
                onClick={confirmar}
                disabled={enviando || Boolean(problema)}
                title={problema ?? undefined}
              >
                {enviando ? 'Redefinindo…' : 'Redefinir senha'}
              </button>
            </div>
          </>
        )}
      </div>
    </Dialogo>
  );
}
